import { AvtGateway, HttpRequest } from '../../../kernel/avt.gateway'
import { BaseRole, BaseRoleOld, RolePermissions } from '../domain/role'
import { RoleRepository } from '../domain/role.repository'

type AvtRole = {
  id: string
  name: string
  description: string
  users_quantity?: number
  super_admin: boolean
  client_id: number
  created_at: string
  updated_at: string
  hierarchy: number
}
type ResponseGetRoles = {
  data: {
    roles: Array<AvtRole>
  }
}
type ResponseCreateOrUpdateRole = {
  data: {
    role: AvtRole
  }
}

type ResponseRolePermissions = {
  data: {
    permissions: Array<string>
  }
}

export const odinRoleRepository: RoleRepository = {
  getRoles: async (): Promise<BaseRole[] | BaseRoleOld[]> => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: '/avt/api/roles'
    }
    const response: ResponseGetRoles = await AvtGateway.executeGetRequest<ResponseGetRoles>({ ...request })
    const roles: BaseRole[] = response.data.roles.map((role: AvtRole) => ({
      id: role.id,
      name: role.name,
      description: role.description,
      hierarchy: role.hierarchy,
      superAdmin: role.super_admin || false
    }))
    return roles
  },
  createRole: async (newRole: BaseRole): Promise<BaseRole> => {
    const request: HttpRequest = {
      method: 'POST',
      endpoint: '/avt/api/roles',
      body: {
        ...newRole
      }
    }
    const response: ResponseCreateOrUpdateRole = await AvtGateway.executePostRequest<ResponseCreateOrUpdateRole>({
      ...request
    })
    const role: BaseRole = {
      id: response.data.role.id,
      name: response.data.role.name,
      description: response.data.role.description,
      superAdmin: response.data.role.super_admin || false
    }
    return role
  },
  updateRole: async (updatedRole: BaseRole): Promise<BaseRole> => {
    const request: HttpRequest = {
      method: 'PATCH',
      endpoint: `/avt/api/roles/${updatedRole.id}`,
      body: {
        name: updatedRole.name,
        description: updatedRole.description
      }
    }

    const response: ResponseCreateOrUpdateRole = await AvtGateway.executePatchRequest<ResponseCreateOrUpdateRole>({
      ...request
    })
    const role: BaseRole = {
      id: response.data.role.id,
      name: response.data.role.name,
      description: response.data.role.description,
      superAdmin: response.data.role.super_admin || false
    }
    return role
  },
  getRolePermissions: async (roleId: string): Promise<RolePermissions> => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/avt/api/roles/${roleId}/permissions`
    }

    const response: ResponseRolePermissions = await AvtGateway.executeGetRequest<ResponseRolePermissions>({
      ...request
    })
    const rolePermissions: RolePermissions = {
      permissions: response.data.permissions
    }
    return rolePermissions
  },
  assignRolePermissions: async (roleId: string, permissions: string[]): Promise<boolean> => {
    const request: HttpRequest = {
      method: 'POST',
      endpoint: `/avt/api/roles/${roleId}/permissions`,
      body: {
        permissions
      }
    }
    const response: ResponseRolePermissions = await AvtGateway.executePostRequest<ResponseRolePermissions>({
      ...request
    })
    if (response.data.permissions.length === permissions.length) {
      return true
    }
    return false
  },
  removeRolePermissions: async (roleId: string, permissions: string[]): Promise<boolean> => {
    const request: HttpRequest = {
      method: 'DELETE',
      endpoint: `/avt/api/roles/${roleId}/permissions`,
      body: {
        permissions
      }
    }

    const response: ResponseRolePermissions = await AvtGateway.executeDeleteRequest<ResponseRolePermissions>({
      ...request
    })
    if (response.data.permissions.length === permissions.length) {
      return true
    }
    return false
  },
  changeRoleHierarchy: async (roleId: string, newHierarchy: number): Promise<BaseRole> => {
    const request: HttpRequest = {
      method: 'PATCH',
      endpoint: `/avt/api/roles/${roleId}/hierarchy`,
      body: {
        hierarchy: newHierarchy
      }
    }

    const response = await AvtGateway.executePatchRequest<ResponseCreateOrUpdateRole>({
      ...request
    })

    return {
      id: response.data.role.id,
      name: response.data.role.name,
      description: response.data.role.description,
      superAdmin: response.data.role.super_admin,
      hierarchy: response.data.role.hierarchy
    }
  }
}
